
import { supabase } from "@/integrations/supabase/client";

/**
 * Vérifie l'email d'un signataire de la charte et envoie l'email officiel
 */
export const verifyCharterSignatory = async (signatoryId: string, verificationToken: string) => {
  console.log('[DEBUG verifyCharterSignatory] Vérification de l\'email avec token:', { signatoryId, verificationToken });
  
  try {
    // Vérifier si le signataire existe avec ce token
    const { data: signatoryData, error: fetchError } = await supabase
      .from('charter_signatories')
      .select('*')
      .eq('id', signatoryId)
      .eq('verification_token', verificationToken)
      .maybeSingle(); // Utiliser maybeSingle() au lieu de single()
    
    if (fetchError) {
      console.error('[DEBUG verifyCharterSignatory] Erreur lors de la récupération du signataire:', fetchError);
      throw new Error('Impossible de vérifier votre email. Veuillez réessayer plus tard.');
    }
    
    if (!signatoryData) {
      console.error('[DEBUG verifyCharterSignatory] Aucun signataire trouvé avec ce token ou cette combinaison id/token');
      
      // Vérifier si le signataire existe sans le token (peut-être déjà vérifié)
      const { data: existingSignatory } = await supabase
        .from('charter_signatories')
        .select('*')
        .eq('id', signatoryId)
        .maybeSingle();
        
      if (existingSignatory && existingSignatory.email_verified) {
        return { 
          success: true, 
          alreadyVerified: true,
          redirectUrl: '/' 
        };
      }
      
      throw new Error('Lien de vérification invalide ou déjà utilisé.');
    }
    
    // Vérification supplémentaire pour voir si la signature est déjà vérifiée
    if (signatoryData.email_verified) {
      console.log('[DEBUG verifyCharterSignatory] La signature a déjà été vérifiée');
      return { 
        success: true, 
        alreadyVerified: true,
        redirectUrl: '/' 
      };
    }
    
    // Mettre à jour le statut de vérification
    const { error: updateError } = await supabase
      .from('charter_signatories')
      .update({ 
        email_verified: true 
      })
      .eq('id', signatoryId);
    
    if (updateError) {
      console.error('[DEBUG verifyCharterSignatory] Erreur lors de la mise à jour du statut de vérification:', updateError);
      throw new Error('Impossible de vérifier votre email. Veuillez réessayer plus tard.');
    }
    
    // Vérification immédiate que le statut a bien été mis à jour
    const { data: verifiedStatus, error: verificationCheckError } = await supabase
      .from('charter_signatories')
      .select('email_verified')
      .eq('id', signatoryId)
      .single();
      
    if (verificationCheckError) {
      console.error('[DEBUG verifyCharterSignatory] Erreur lors de la vérification du statut après mise à jour:', verificationCheckError);
    } else {
      console.log('[DEBUG verifyCharterSignatory] Statut après mise à jour:', verifiedStatus);
    }
    
    console.log('[DEBUG verifyCharterSignatory] Vérification réussie, tentative d\'envoi de l\'email officiel');
    
    // Obtenir le nombre total de signatures pour le compteur
    const { count, error: countError } = await supabase
      .from('charter_signatories')
      .select('*', { count: 'exact', head: true })
      .eq('email_verified', true); // Ne compter que les signatures vérifiées
    
    if (countError) {
      console.error('[DEBUG verifyCharterSignatory] Erreur lors du comptage des signatures:', countError);
      // Continuer malgré l'erreur
    }
    
    // Tentative d'envoi de l'email officiel avec le compteur via la fonction Edge
    try {
      const url = "https://jqswmyqzmjdstetjqzvv.supabase.co/functions/v1/send-charter-confirmation";
      const apiKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Impxc3dteXF6bWpkc3RldGpxenZ2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzcyMzE2NTYsImV4cCI6MjA1MjgwNzY1Nn0.JLDD0eYGJ0G_8IL5BUR7NI6iEOQyNSMIxz4PLwSO0G4";
      
      console.log('[DEBUG verifyCharterSignatory] Données avant envoi:', {
        firstName: signatoryData.first_name,
        lastName: signatoryData.last_name,
        email: signatoryData.email,
        signatoryId: signatoryData.id,
        signatureCount: count || 0,
        signatureNumber: signatoryData.signature_number
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          firstName: signatoryData.first_name,
          lastName: signatoryData.last_name,
          email: signatoryData.email,
          signatoryId: signatoryData.id,
          signatureCount: count || 0,
          signatureNumber: signatoryData.signature_number
        }),
      });
      
      // Même si la réponse n'est pas 2xx, on considère quand même que la vérification a réussi
      // puisque nous avons mis à jour la base de données
      const emailResult = await response.json().catch(() => ({ success: false }));
      console.log('[DEBUG verifyCharterSignatory] Réponse de la fonction d\'envoi d\'email officiel:', emailResult);
      
      // Vérifier à nouveau que le statut a bien été mis à jour
      const { data: verifiedData, error: finalCheckError } = await supabase
        .from('charter_signatories')
        .select('email_verified, signature_number')
        .eq('id', signatoryId)
        .single();
        
      console.log('[DEBUG verifyCharterSignatory] État final après tout le processus:', verifiedData);
      
      if (finalCheckError) {
        console.error('[DEBUG verifyCharterSignatory] Erreur lors de la vérification finale:', finalCheckError);
      }
      
      return { 
        success: true, 
        signatureCount: count || 0,
        signatureNumber: signatoryData.signature_number,
        emailSent: response.ok,
        emailVerified: verifiedData?.email_verified || false,
        redirectUrl: '/' // URL de redirection vers la page d'accueil
      };
    } catch (emailError) {
      console.error('[DEBUG verifyCharterSignatory] Erreur attrapée lors de l\'envoi de l\'email officiel:', emailError);
      
      // Vérifier une dernière fois l'état de la vérification
      const { data: finalState } = await supabase
        .from('charter_signatories')
        .select('email_verified, signature_number')
        .eq('id', signatoryId)
        .single();
      
      console.log('[DEBUG verifyCharterSignatory] État final après erreur d\'envoi d\'email:', finalState);
      
      // Malgré l'erreur d'envoi d'email, la vérification a réussi
      return { 
        success: true, 
        signatureCount: count || 0,
        signatureNumber: signatoryData.signature_number,
        emailError: true,
        emailVerified: finalState?.email_verified || false,
        redirectUrl: '/' 
      };
    }
  } catch (error) {
    console.error('[DEBUG verifyCharterSignatory] Erreur attrapée:', error);
    throw error;
  }
}
